import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import { getAllProductsService } from "redux/services/general_service";
import { useTranslation } from "react-i18next";
import {
  clearFilters,
  setFilterData,
  setProductNull,
} from "redux/slices/general";
import Skeleton from "react-loading-skeleton"; // استيراد Skeleton

const FilterPanel = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  isMobile = false,
  setProducts,
  setCurrentPage,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const { categoriesList } = useSelector((state) => state.general);

  const [expandedSections, setExpandedSections] = useState({
    category: true,
    price: true,
    brand: true,
    rating: true,
    features: false,
  });
  // هنا نضيف تعريف expandedCategories
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleFilterChange = (type, value, checked) => {
    setLocalFilters((prev) => {
      const newFilters = { ...prev };

      if (type === "category" || type === "brand" || type === "features") {
        if (!newFilters[type]) newFilters[type] = [];

        if (checked) {
          newFilters[type] = [...newFilters[type], value];
        } else {
          newFilters[type] = newFilters[type].filter((item) => item !== value);
        }
      } else if (type === "priceRange") {
        if (checked) {
          newFilters.priceRange = value;
        } else {
          newFilters.priceRange = null;
        }
      } else if (type === "rating") {
        if (checked) {
          newFilters.rating = value;
        } else {
          newFilters.rating = null;
        }
      } else if (type === "customPrice") {
        newFilters.customPrice = value;
        newFilters.priceRange = null; // Clear preset range when custom is used
      }

      return newFilters;
    });
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      category: [],
      brand: [],
      priceRange: null,
      customPrice: { price_start: "", price_end: "" },
      rating: null,
      features: [],
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    dispatch(clearFilters());
    dispatch(getAllProductsService({ page: 1 }));
    setProducts([]);
    setCurrentPage(1);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.category?.length) count += localFilters.category.length;
    if (localFilters.brand?.length) count += localFilters.brand.length;
    if (localFilters.features?.length) count += localFilters.features.length;
    if (
      localFilters.priceRange ||
      localFilters.customPrice?.price_start ||
      localFilters.customPrice?.price_end
    )
      count += 1;
    if (localFilters.rating) count += 1;
    return count;
  };

  const formatParams = (filters) => {
    const params = new URLSearchParams();
    if (filters.category?.length) {
      filters.category.forEach((catId, index) => {
        params.append(`categories[${index + 1}]`, catId);
      });
    }
    if (filters.customPrice?.price_start) {
      params.append("price_start", filters.customPrice.price_start);
    }
    return params.toString();
  };

  const handleApplyFilters = () => {
    setProducts([]);
    setCurrentPage(1);
    dispatch(setProductNull());
    const queryString = formatParams(localFilters);
    const params = new URLSearchParams(queryString);
    dispatch(setFilterData({ ...Object.fromEntries(params) }));
  //  alert(params)
    dispatch(getAllProductsService({ page: 1, ...Object.fromEntries(params) }));
    if(isMobile){
      onClose()
    }
  };

  useEffect(() => {
    console.log(localFilters);
  }, [localFilters]);

  const FilterSection = ({ title, children, sectionKey }) => (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-smooth"
      >
        <h3 className="font-medium text-text-primary">{t(title)}</h3>
        <Icon
          name="ChevronDown"
          size={16}
          className={`text-text-secondary transition-transform ${
            expandedSections[sectionKey] ? "rotate-180" : ""
          }`}
        />
      </button>
      {expandedSections[sectionKey] && (
        <div className="px-4 pb-4">{children}</div>
      )}
    </div>
  );

  const content = (
    <div className="flex flex-col h-full bg-white shadow-lg rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-gray-100 rounded-t-lg shadow-lg">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <h2 className="text-2xl font-extrabold text-transparent bg-clip-text text-global-4">
              {t("filters")}
            </h2>
          </div>

          {getActiveFiltersCount() > 0 && (
            <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-lg transform hover:scale-110 transition-transform duration-200 flex items-center justify-center min-w-[32px]">
              {getActiveFiltersCount()}
            </span>
          )}
        </div>

        {isMobile && (
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        )}
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Categories */}
        <FilterSection title="categories" sectionKey="category">
          <div className="space-y-2">
            {categoriesList?.map((category) => (
              <div key={category.id}>
                <div className="flex items-center justify-between w-full pr-2 pl-2 cursor-pointer hover:bg-gray-100 rounded-md text-right">
                  <Checkbox
                    className="flex items-center justify-between w-full"
                    label={
                      <span
                        onClick={() => toggleCategory(category.id)}
                        className="pr-2 pl-2"
                      >
                        {category.name}
                      </span>
                    }
                    checked={
                      localFilters.category?.includes(category.id) || false
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handleFilterChange(
                        "category",
                        category.id,
                        e.target.checked
                      );
                    }}
                    // منع التوقف عند الضغط على الـ checkbox فقط
                    //   onClick={(e) => e.stopPropagation()}
                  />
                  <Icon
                    onClick={() => toggleCategory(category.id)}
                    name="ChevronDown"
                    size={16}
                    className={`text-text-secondary transition-transform ${
                      expandedCategories[category.id] ? "rotate-180" : ""
                    }`}
                  />
                </div>

                {/* عرض الـ sub_categories إذا الكاتيجوري مفتوح */}
                {expandedCategories[category.id] &&
                  category.sub_categories?.length > 0 && (
                    <div className="mt-1 space-y-1 flex-col items-center justify-center gap-2 text-right">
                      {category.sub_categories.map((sub) => (
                        <Checkbox
                          className="flex items-center justify-between w-full pr-5 pl-5"
                          key={sub.id}
                          label={
                            <span className="text-sm text-text-secondary pr-2 pl-2">
                              {sub.name}
                            </span>
                          }
                          checked={
                            localFilters.category?.includes(sub.id) || false
                          }
                          onChange={(e) =>
                            handleFilterChange(
                              "category",
                              sub.id,
                              e.target.checked
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        </FilterSection>

        {/* Price Range */}
        <FilterSection title="priceRange" sectionKey="price">
          <div className="space-y-3 text-right">
            {/* Custom Range */}
            <div className="pt-2 border-t border-border">
              <p className="text-sm font-medium text-text-primary mb-2">
                {t("customRange")}
              </p>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder={t("min")}
                  value={localFilters.customPrice?.price_start || ""}
                  onChange={(e) =>
                    handleFilterChange("customPrice", {
                      ...localFilters.customPrice,
                      price_start: e.target.value,
                    })
                  }
                  className="flex-1 border border-border rounded-md p-2"
                />
                <span className="text-text-secondary">-</span>
                <Input
                  type="number"
                  placeholder={t("max")}
                  value={localFilters.customPrice?.price_end || ""}
                  onChange={(e) =>
                    handleFilterChange("customPrice", {
                      ...localFilters.customPrice,
                      price_end: e.target.value,
                    })
                  }
                  className="flex-1 border border-border rounded-md p-2"
                />
              </div>
            </div>
          </div>
        </FilterSection>

        {/* Footer Actions */}
        <div className="border-t border-border  p-4 bg-gray-100">
          <div className="flex flex-col space-y-2">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              fullWidth
              className="border px-[24px] sm:px-[36px] lg:px-[48px] py-[6px] sm:py-[9px] lg:py-[12px] flex items-center gap-[4px] sm:gap-[6px] lg:gap-[8px] text-[11px] sm:text-[12px] lg:text-[14px]"
            >
              {t("clearAll")}
            </Button>
            <Button
              variant="default"
              onClick={handleApplyFilters}
              fullWidth
              className="border border-global-text4 px-[24px] sm:px-[36px] lg:px-[48px] py-[6px] sm:py-[9px] lg:py-[12px] flex items-center gap-[4px] sm:gap-[6px] lg:gap-[8px] text-[11px] sm:text-[12px] lg:text-[14px]"
            >
              {t("applyFilters")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div className="fixed inset-0 bg-black/50 z-50">
            <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-lg rounded-lg">
              {content}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="w-64 bg-white border-r border-border h-full shadow-lg rounded-lg">
      {content}
    </div>
  );
};

export default FilterPanel;
